/*
 * AAIKO PDP details drawer.
 * Figma 1210:25759 (desktop, 480px panel) and 1210:26546 (mobile, 300px panel).
 *
 * The markup ships as <details>/<summary>, which already works without JS.
 * This upgrade moves each panel to <body> so a fixed overlay is not trapped by
 * an ancestor `container-type` / `contain: layout` (both create a containing
 * block for position:fixed), then drives open/close itself: scrim, focus trap,
 * Escape, scroll lock, focus returned to the trigger.
 */
(function () {
  if (window.customElements && customElements.get('aaiko-details')) return;

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  var scrim = null;
  var current = null; // { panel, trigger }

  function getScrim() {
    if (scrim && scrim.isConnected) return scrim;
    scrim = document.createElement('div');
    scrim.className = 'aaiko-details__scrim';
    scrim.setAttribute('data-aaiko-details-scrim', '');
    scrim.addEventListener('click', function () {
      closeCurrent();
    });
    document.body.appendChild(scrim);
    return scrim;
  }

  function focusable(panel) {
    return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function openPanel(panel, trigger) {
    if (current && current.panel !== panel) closeCurrent(true);
    getScrim().setAttribute('data-open', '');
    panel.setAttribute('data-open', '');
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('aaiko-details-locked');
    current = { panel: panel, trigger: trigger };
    // Focus the dialog itself, not the close button: the trap starts at the
    // top of the panel and no focus ring is painted on a pointer-driven open.
    panel.focus({ preventScroll: true });
  }

  function closeCurrent(silent) {
    if (!current) return;
    var panel = current.panel;
    var trigger = current.trigger;
    current = null;
    panel.removeAttribute('data-open');
    trigger.setAttribute('aria-expanded', 'false');
    if (!silent) {
      if (scrim) scrim.removeAttribute('data-open');
      document.documentElement.classList.remove('aaiko-details-locked');
      trigger.focus({ preventScroll: true });
    }
  }

  document.addEventListener('keydown', function (event) {
    if (!current) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeCurrent();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusable(current.panel);
    if (!items.length) {
      event.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (!current.panel.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  function AaikoDetails() {
    return Reflect.construct(HTMLElement, [], AaikoDetails);
  }
  AaikoDetails.prototype = Object.create(HTMLElement.prototype);
  AaikoDetails.prototype.constructor = AaikoDetails;
  Object.setPrototypeOf(AaikoDetails, HTMLElement);

  AaikoDetails.prototype.connectedCallback = function () {
    if (this.aaikoReady) return;
    this.aaikoReady = true;
    var self = this;

    Array.prototype.forEach.call(this.querySelectorAll('[data-aaiko-details-panel]'), function (panel) {
      var item = panel.parentElement;
      var trigger = item && item.querySelector('[data-aaiko-details-toggle]');
      if (!trigger) return;

      panel.classList.add('aaiko-details__panel--drawer');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('tabindex', '-1');
      document.body.appendChild(panel);
      self.setAttribute('data-aaiko-details-enhanced', '');

      function toggle(event) {
        event.preventDefault();
        if (current && current.panel === panel) closeCurrent();
        else openPanel(panel, trigger);
      }

      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') toggle(event);
      });

      var close = panel.querySelector('[data-aaiko-details-close]');
      if (close) {
        close.addEventListener('click', function () {
          closeCurrent();
        });
      }
    });
  };

  customElements.define('aaiko-details', AaikoDetails);
})();
